<template>
  <div class="dashboard_kpi-overview">
    <i18n class="dashboard_kpi-overview__headline" path="kpi.overviewTitle.your" tag="h2">
      <template v-slot:companyName>{{ company_name }}</template>
      <template v-slot:0
        ><b>{{ $t('kpi.overviewTitle.kpiOverview') }}</b></template
      >
    </i18n>

    <SliderController
      #default="{ sliderProps }"
      class="dashboard_kpi-overview__inner"
      :childs-are-slides="false"
      :active-slide-index="selectedLocalIndex"
      @switchState="switchKpi"
    >
      <RadarChart
        class="dashboard_kpi-overview__chart"
        :kpi-data="kpiData"
        :ppp-active-sections="pppActiveSections"
        :kpi-active-index="selectedLocalIndex"
        :show-k-p-i-labels="true"
        :active-k-p-i-section-index="sectionIndex"
        @click-kpi="selectIndex"
      />
      <!-- horizontal slider -->
      <div
        class="dashboard_kpi-overview__kpi-rating-group dashboard_kpi-overview__kpi-rating-group-horizontal"
        v-bind="sliderProps"
      >
        <!-- TODO: maybe use Slide component in KpiRating as root element -->
        <KpiRating
          v-for="rating in kpiRatings"
          :key="rating.id"
          :unit-is-reversed="rating.reversed"
          :sdg-id="rating.id"
          class="dashboard_kpi-overview__kpi-rating-item"
          :label="rating.label"
          :value="rating.value"
          :average-value="rating.averageValue"
          :unit="rating.unit"
        />
      </div>

      <!-- kpi vertical selector TODO: Refactor into component in polishing phase -->
      <div class="dashboard_kpi-overview__kpi-rating-group-vertical-outer">
        <div
          v-show="selectedLocalIndex > 2"
          class="dashboard_kpi-overview__icon-up"
          @click="clickSectionScroll(true)"
        >
          <Icon icon="arrows-down" />
        </div>
        <div class="dashboard_kpi-overview__kpi-rating-group-vertical">
          <div
            class="dashboard_kpi-overview__kpi-rating-group-vertical__inner"
            :style="{ 'margin-top': sectionIndex * -380 + 'px' }"
          >
            <div
              v-for="(rating, index) in kpiRatings"
              :key="rating.id"
              class="ashboard_kpi-overview__kpi-rating-item__outer"
              @click="switchKpi(index)"
            >
              <KpiRating
                :unit-is-reversed="rating.reversed"
                :sdg-id="rating.id"
                class="dashboard_kpi-overview__kpi-rating-item"
                :label="rating.label"
                :value="rating.value"
                :average-value="rating.averageValue"
                :unit="rating.unit"
                :class="{
                  'dashboard_kpi-overview__kpi-rating-item-active': index == selectedLocalIndex,
                }"
                :style="[
                  index == selectedLocalIndex
                    ? { 'border-color': activePPPColor }
                    : { 'border-color': 'transparent' },
                ]"
              />
            </div>
          </div>
        </div>
        <div
          v-if="selectedLocalIndex < 6"
          class="dashboard_kpi-overview__icon-down"
          @click="clickSectionScroll(false)"
        >
          <Icon icon="arrows-down" />
        </div>
      </div>
    </SliderController>
  </div>
</template>

<script>
import { getKpiRatingConfigs } from './kpiConfig';

import store, { impactDistribution, getRadarChartData, getCompanyName } from '../../store';
import { playSound } from '../../services/sound';

import { pppSections } from '../../store/trivia';

import KpiRating from '../KpiRating';
import RadarChart from '../radarChart/RadarChart';
import SliderController from '../SliderController';
import Icon from '../Icon';

export default {
  name: 'DashboardKpiOverview',

  components: {
    KpiRating,
    RadarChart,
    SliderController,
    Icon,
  },

  data() {
    return {
      store,
      kpiData: getRadarChartData(),
      selectedLocalIndex: 0,
      selectSectionIndex: 0,
      currentRatingId: 'co2',
    };
  },

  beforeRouteEnter(from, to, next) {
    window.scrollTo(0, 0);
    next();
  },

  computed: {
    impactDistribution,
    company_name: getCompanyName,

    pppActiveSections: () => Object.values(pppSections),

    sectionIndex() {
      let index = 0;
      if (this.selectedLocalIndex < 2) {
        index = 0;
      }
      if (this.selectedLocalIndex > 2 && this.selectedLocalIndex <= 5) {
        index = 1;
      }
      if (this.selectedLocalIndex > 5) {
        index = 2;
      }
      return index;
    },

    activePPPColor() {
      let activeColor = pppSections.PEOPLE.color;
      if (this.selectedLocalIndex < 3) {
        activeColor = pppSections.PLANET.color;
      }
      if (this.selectedLocalIndex > 2 && this.selectedLocalIndex <= 5) {
        activeColor = pppSections.PROFIT.color;
      }
      if (this.selectedLocalIndex > 5) {
        activeColor = pppSections.PEOPLE.color;
      }
      return activeColor;
    },

    kpiRatings() {
      const kpiAverageValueGaps = store.kpiAverageValueGaps();
      const kpiRatingConfigs = getKpiRatingConfigs();
      return Object.entries(store.userModel.kpiPercentages).map(([id, value]) => {
        const config = kpiRatingConfigs[id];
        if (config === undefined) return undefined;

        let isReversed = false;
        if (id === 'co2' || id === 'energy' || id === 'plastic') {
          isReversed = true;
        }

        const { getValue, ...configRest } = config;

        return {
          id,
          ...configRest,
          value: getValue(value),
          averageValue: kpiAverageValueGaps[id],
          reversed: isReversed,
        };
      });
    },
  },

  created() {
    setTimeout(() => {
      this.selectedLocalIndex = 3;
    }, 10);
    setTimeout(() => {
      this.selectedLocalIndex = 0;
    }, 20);
  },

  mounted() {
    window.scrollTo(0, 0);
    playSound('fxA', 'user');
  },

  methods: {
    selectIndex(kpiId) {
      // TODO: refactor
      if (kpiId === '2_0') {
        this.selectedLocalIndex = 0;
      }
      if (kpiId === '2_1') {
        this.selectedLocalIndex = 1;
      }
      if (kpiId === '2_2') {
        this.selectedLocalIndex = 2;
      }
      if (kpiId === '1_0') {
        this.selectedLocalIndex = 3;
      }
      if (kpiId === '1_1') {
        this.selectedLocalIndex = 4;
      }
      if (kpiId === '1_2') {
        this.selectedLocalIndex = 5;
      }
      if (kpiId === '0_0') {
        this.selectedLocalIndex = 6;
      }
      if (kpiId === '0_1') {
        this.selectedLocalIndex = 7;
      }
      if (kpiId === '0_2') {
        this.selectedLocalIndex = 8;
      }
    },

    switchKpi(kpiIndex) {
      this.selectedLocalIndex = kpiIndex;
    },

    clickSectionScroll(scrollUp) {
      if (scrollUp) {
        if (this.selectedLocalIndex > 5) {
          this.selectedLocalIndex = 3;
        } else if (this.selectedLocalIndex > 2 && this.selectedLocalIndex < 6) {
          this.selectedLocalIndex = 0;
        }
      } else if (this.selectedLocalIndex < 3) {
        this.selectedLocalIndex = 3;
      } else if (this.selectedLocalIndex > 2 && this.selectedLocalIndex < 6) {
        this.selectedLocalIndex = 6;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dashboard_kpi-overview {
  position: relative;
}

.dashboard_kpi-overview__chart {
  margin-top: 20px;
  margin-bottom: 40px;
  margin-left: auto;
  margin-right: auto;
}

.dashboard_kpi-overview__headline {
  @include font-headline;
  margin: 40px auto 20px;
  padding: 20px;
  width: 320px;
  text-align: center;
}

.dashboard_kpi-overview__inner {
  padding-bottom: 5px;
}

.dashboard_kpi-overview__kpi-rating-item,
.dashboard_kpi-overview__company_info,
.dashboard_kpi-overview__global_info {
  @include transparent-layer;
}

.dashboard_kpi-overview__focus_info {
  padding: 20px;
  text-align: center;
}

.dashboard_kpi-overview__kpi-rating-group-vertical-outer {
  position: absolute;
  width: 450px;
  height: 376px;
  top: 50%;
  left: 50%;
  margin-left: 350px;
  margin-top: -100px;
  overflow: visible;
  z-index: 1000;
}

.dashboard_kpi-overview__kpi-rating-group-vertical {
  position: relative;
  width: 450px;
  height: 376px;
  overflow: hidden;
  display: none;

  .dashboard_kpi-overview__kpi-rating-item {
    margin-bottom: 10px;
    border: 1px solid transparent;
    cursor: pointer;
    pointer-events: all;
    opacity: 0.5;
    z-index: 1000;
  }

  .dashboard_kpi-overview__kpi-rating-item-active {
    border: 1px $color-purple solid;
    opacity: 1;
    transition: all 0.5s ease;
  }
}

.dashboard_kpi-overview__icon-down {
  position: absolute;
  left: 50%;
  bottom: -45px;
  margin-left: -10px;
  cursor: pointer;
  z-index: 1000;
}

.dashboard_kpi-overview__icon-up {
  position: absolute;
  left: 50%;
  top: -45px;
  margin-left: -10px;
  transform: rotate(180deg);
  cursor: pointer;
  z-index: 1000;
}

.dashboard_kpi-overview__kpi-rating-group-vertical__inner {
  margin-top: 0;
  transition: margin-top 0.5s ease;
}

@include helper__greater($bp-1024) {
  .dashboard_kpi-overview {
    margin-top: -15px;
  }
  .dashboard_kpi-overview__kpi-rating-group-vertical {
    display: block;
  }
  .dashboard_kpi-overview__kpi-rating-group-horizontal {
    display: none;
  }
  .dashboard_kpi-overview__inner {
    margin-left: -400px;
  }
  .dashboard_kpi-overview__kpi-rating-group-vertical-outer {
    margin-left: 70px;
  }
}

@include helper__greater($bp-1590) {
  .dashboard_kpi-overview__inner {
    margin-left: 0px;
  }
  .dashboard_kpi-overview__kpi-rating-group-vertical-outer {
    margin-left: 350px;
  }
}

@include helper__greater($bp-2560) {
  .dashboard_kpi-overview {
    margin-top: 20%;
  }
}
</style>
